import { useState } from 'react';
import { trpc } from '@/lib/trpc';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { GitBranch, Save, RotateCcw, Trash2, Calendar, User, FileText } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';

interface BoardVersionsPanelProps {
  boardId: number;
  onRestore?: (versionId: number) => void;
}

export function BoardVersionsPanel({ boardId, onRestore }: BoardVersionsPanelProps) {
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [description, setDescription] = useState('');
  const utils = trpc.useUtils();

  const { data: versions, isLoading } = trpc.taskManager.boardVersions.list.useQuery({ boardId });
  const { data: latestVersion } = trpc.taskManager.boardVersions.latest.useQuery({ boardId });

  const createVersion = trpc.taskManager.boardVersions.create.useMutation({
    onSuccess: () => {
      toast.success('Versão criada com sucesso');
      utils.taskManager.boardVersions.list.invalidate({ boardId });
      setShowCreateDialog(false);
      setDescription('');
    },
    onError: (error) => {
      toast.error(`Erro ao criar versão: ${error.message}`);
    },
  });

  const deleteVersion = trpc.taskManager.boardVersions.delete.useMutation({
    onSuccess: () => {
      toast.success('Versão deletada');
      utils.taskManager.boardVersions.list.invalidate({ boardId });
    },
    onError: (error) => {
      toast.error(`Erro ao deletar versão: ${error.message}`);
    },
  });

  const handleCreateVersion = async () => {
    if (!description.trim()) {
      toast.error('Descrição é obrigatória');
      return;
    }

    // Buscar dados atuais do board para snapshot
    const boardData = await utils.taskManager.boards.get.fetch({ id: boardId });
    const groups = await utils.taskManager.groups.listByBoard.fetch({ boardId });
    const items = await utils.taskManager.items.listByBoard.fetch({ boardId });
    const columns = await utils.taskManager.columns.listByBoard.fetch({ boardId });

    const snapshotData = JSON.stringify({
      board: boardData,
      groups,
      items,
      columns,
      timestamp: new Date().toISOString(),
    });

    const versionNumber = (latestVersion?.version_number || 0) + 1;

    createVersion.mutate({
      boardId,
      versionNumber,
      snapshotData,
      description,
    });
  };

  const handleRestore = (versionId: number) => {
    if (confirm('Tem certeza que deseja restaurar esta versão? As alterações atuais serão perdidas.')) {
      onRestore?.(versionId);
    }
  };

  const handleDelete = (versionId: number) => {
    if (confirm('Tem certeza que deseja deletar esta versão?')) {
      deleteVersion.mutate({ versionId });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-muted-foreground">Carregando versões...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <GitBranch className="h-5 w-5" />
          <h3 className="font-semibold">Versões do Board</h3>
          {versions && <Badge variant="secondary">{versions.length}</Badge>}
        </div>
        <Button onClick={() => setShowCreateDialog(true)} size="sm">
          <Save className="h-4 w-4 mr-2" />
          Criar Versão
        </Button>
      </div>

      <ScrollArea className="flex-1">
        <div className="p-4 space-y-3">
          {!versions || versions.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-64 text-muted-foreground">
              <GitBranch className="h-12 w-12 mb-2 opacity-50" />
              <p>Nenhuma versão salva</p>
              <p className="text-sm">Crie uma versão para salvar o estado atual do board</p>
            </div>
          ) : (
            versions.map((version: any, index: number) => (
              <div
                key={version.id}
                className="border rounded-lg p-4 hover:bg-muted/50 transition-colors"
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <Badge variant={index === 0 ? 'default' : 'outline'}>
                      v{version.version_number}
                    </Badge>
                    {index === 0 && (
                      <Badge variant="secondary" className="text-xs">
                        Mais recente
                      </Badge>
                    )}
                  </div>
                  <div className="flex gap-1">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRestore(version.id)}
                      disabled={index === 0}
                    >
                      <RotateCcw className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(version.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-start gap-2">
                    <FileText className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                    <p className="text-sm">{version.description}</p>
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <User className="h-3 w-3" />
                    {version.created_by_name}
                  </div>

                  <div className="flex items-center gap-2 text-xs text-muted-foreground">
                    <Calendar className="h-3 w-3" />
                    {formatDistanceToNow(new Date(version.created_at), {
                      addSuffix: true,
                      locale: ptBR,
                    })}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </ScrollArea>

      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Criar Nova Versão</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Número da Versão</Label>
              <Input
                value={`v${(latestVersion?.version_number || 0) + 1}`}
                disabled
              />
            </div>

            <div className="space-y-2">
              <Label>Descrição *</Label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descreva as alterações desta versão..."
                rows={4}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
              Cancelar
            </Button>
            <Button
              onClick={handleCreateVersion}
              disabled={!description.trim() || createVersion.isPending}
            >
              {createVersion.isPending ? 'Criando...' : 'Criar Versão'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
